import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Badge, EmptyState, LoadingState, ErrorState } from "@/components/design-system";
import { listOwnCampaigns } from "@/lib/campaigns";
import { getLatestReport } from "@/lib/aiReports";
import { formatApiError } from "@/lib/api";

export default function ReportsIndex() {
  const [state, setState] = useState({ status: "loading", rows: [], err: "" });

  useEffect(() => {
    (async () => {
      try {
        const campaigns = await listOwnCampaigns();
        const rows = await Promise.all(campaigns.map(async (c) => {
          try {
            const data = await getLatestReport(c.id);
            return { campaign: c, ...data };
          } catch { return { campaign: c, report: null }; }
        }));
        setState({ status: "ready", rows, err: "" });
      } catch (err) { setState({ status: "error", rows: [], err: formatApiError(err) }); }
    })();
  }, []);

  return (
    <div data-testid="feedback-reports-index">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Feedback reports</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Every campaign&apos;s insight report at a glance.</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Each report is drafted directly from the community feedback you receive — themes, verbatims, and next-step suggestions clustered from your validators. Reports are <b>private to you</b>. Nothing is ever shared or published.</p>

      {state.status === "loading" && <LoadingState label="Loading reports…" />}
      {state.status === "error" && <ErrorState detail={state.err} />}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState title="No campaigns yet" description="Start a validation campaign first — reports become available after 3 approved submissions." action={<Link to="/app/campaigns"><Button variant="primary">Go to campaigns</Button></Link>} />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <div className="mt-8 space-y-4" data-testid="feedback-reports-list">
          {state.rows.map(({ campaign, report, approved_count, min_required, can_generate }) => (
            <Card key={campaign.id} className="!p-6" data-testid={`feedback-reports-row-${campaign.id}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={campaign.status === "open" ? "active" : "neutral"}>{campaign.status}</Badge>
                    <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{campaign.template_name}</span>
                    {report && <Badge variant={report.status === "ready" ? "active" : report.status === "generating" ? "neutral" : "admin"}>report {report.status}</Badge>}
                    {report && report.status === "ready" && !report.read_at && (
                      <Badge variant="premium" data-testid={`feedback-report-new-${campaign.id}`}>New</Badge>
                    )}
                  </div>
                  <h3 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A] mt-2">{campaign.name}</h3>
                  <p className="text-sm text-[#0B1F3A]/70 mt-1">On {campaign.product_name}</p>
                  {!report && <div className="text-xs text-[#0B1F3A]/60 mt-2">{approved_count} / {min_required} approved submissions {can_generate ? "· ready to generate" : "· need more before generating"}</div>}
                  {report && report.recommendation && <p className="mt-3 text-sm text-[#0B1F3A]/85 line-clamp-2 max-w-2xl">💡 {report.recommendation}</p>}
                </div>
                <Link to={`/app/campaigns/${campaign.id}`}><Button variant="secondary">Open</Button></Link>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
